import { useEffect, useState } from "react";
import { Lock, LogOut, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "family-passcode";

export default function FamilyPage() {
  const [passcode, setPasscode] = useState(
    () => sessionStorage.getItem(KEY) || "",
  );
  const [entered, setEntered] = useState("");
  const [rsvps, setRsvps] = useState(null);
  const [error, setError] = useState("");

  const load = async (code) => {
    setError("");
    try {
      const res = await fetch(`${API}/admin/rsvps`, {
        headers: { "X-Admin-Passcode": code },
      });
      if (res.status === 401) {
        sessionStorage.removeItem(KEY);
        setPasscode("");
        throw new Error("Incorrect passcode");
      }
      if (!res.ok) throw new Error("Could not load RSVPs");
      setRsvps(await res.json());
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    if (passcode) load(passcode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    sessionStorage.setItem(KEY, entered);
    setPasscode(entered);
    load(entered);
  };

  const signOut = () => {
    sessionStorage.removeItem(KEY);
    setPasscode("");
    setRsvps(null);
  };

  if (!passcode) {
    return (
      <main className="zulu-emboss flex min-h-svh items-center justify-center bg-[#4A3B32] px-6">
        <form
          data-testid="family-login-form"
          onSubmit={submit}
          className="w-full max-w-sm text-center"
        >
          <Lock className="mx-auto mb-6 text-[#C5A059]" size={28} />
          <h1 className="font-serif-lux text-3xl font-medium text-[#FAF8F5]">
            Family RSVP List
          </h1>
          <p className="mt-2 text-xs uppercase tracking-[0.3em] text-[#C5A059]">
            Mbele Family only
          </p>
          <input
            data-testid="family-passcode-input"
            type="password"
            value={entered}
            onChange={(e) => setEntered(e.target.value)}
            placeholder="Family passcode"
            className="mt-8 w-full border-b border-[#C5A059]/50 bg-transparent py-3 text-center font-serif-lux text-lg text-[#FAF8F5] placeholder:text-[#FAF8F5]/40 focus:border-[#C5A059] focus:outline-none"
          />
          {error && (
            <p data-testid="family-login-error" className="mt-3 text-sm text-[#E8A0A0]">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="family-login-button"
            className="mt-8 w-full rounded-full bg-[#C5A059] px-8 py-4 text-xs uppercase tracking-[0.25em] text-[#1A1A1A] transition-transform hover:scale-[1.02] active:scale-95"
          >
            View Responses
          </button>
        </form>
      </main>
    );
  }

  const list = rsvps || [];
  const attending = list.filter((r) => r.attending);
  const headcount = attending.reduce((sum, r) => sum + r.guests, 0);

  return (
    <main className="min-h-svh bg-[#FAF8F5] px-6 py-14 text-[#4A3B32]">
      <div className="mx-auto max-w-2xl">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">
              Umbondo · Ingqibamasondo
            </p>
            <h1 className="mt-2 font-serif-lux text-3xl font-medium sm:text-4xl">
              RSVP Responses
            </h1>
          </div>
          <button
            type="button"
            data-testid="family-signout-button"
            onClick={signOut}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#8C6A53] hover:text-[#4A3B32]"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>

        <div className="mt-8 grid grid-cols-3 gap-4" data-testid="family-headcount">
          {[
            ["Responses", list.length],
            ["Attending", attending.length],
            ["Total guests", headcount],
          ].map(([label, value]) => (
            <div
              key={label}
              className="rounded-2xl border border-[#C5A059]/30 bg-white/70 px-4 py-5 text-center"
            >
              <p className="font-serif-lux text-3xl font-medium text-[#4A3B32]">
                {value}
              </p>
              <p className="mt-1 text-[9px] uppercase tracking-[0.2em] text-[#8C6A53]">
                {label}
              </p>
            </div>
          ))}
        </div>

        {error && <p className="mt-6 text-sm text-red-700">{error}</p>}
        {rsvps && list.length === 0 && (
          <p className="mt-10 text-center font-serif-lux text-xl italic text-[#8C6A53]">
            No responses yet — they will appear here as guests reply.
          </p>
        )}

        <div data-testid="family-rsvp-list" className="mt-8 space-y-4">
          {list.map((r) => (
            <div
              key={r.id}
              className="rounded-2xl border border-[#C5A059]/30 bg-white/70 px-5 py-4"
            >
              <div className="flex items-center justify-between gap-3">
                <p className="font-serif-lux text-xl font-medium">{r.name}</p>
                <span
                  className={`shrink-0 rounded-full px-3 py-1 text-[9px] uppercase tracking-[0.2em] ${
                    r.attending
                      ? "bg-[#4A3B32] text-[#FAF8F5]"
                      : "bg-[#5A3A5A] text-[#FAF8F5]"
                  }`}
                >
                  {r.attending ? "Attending" : "Declined"}
                </span>
              </div>
              <div className="mt-1 flex items-center gap-4 text-sm text-[#4A3B32]/70">
                {r.attending && (
                  <span className="flex items-center gap-1">
                    <Users size={13} /> {r.guests} guest{r.guests === 1 ? "" : "s"}
                  </span>
                )}
                <span className="text-xs text-[#8C6A53]">
                  {new Date(r.created_at).toLocaleDateString("en-ZA", {
                    day: "numeric",
                    month: "short",
                  })}
                </span>
              </div>
              {r.note && (
                <p className="mt-2 font-serif-lux text-base italic text-[#5A3A5A]">
                  “{r.note}”
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
